// src/pages/Grow/Intakes/[IntakeDetails]/index.tsx
/**
 * A single public course intake, and the place it is actioned.
 *
 * Layout follows `Counselling/Requests/[RequestDetails]/index.tsx`: Back button, header row carrying
 * Refresh, a profile card whose top-right corner belongs to the primary action, then Tabs inside one
 * shared card.
 *
 * **Participants are real here.** `RegistrationIntakeModelView.participants` is populated, so the
 * People tab lists everyone.
 *
 * ⚠️ **The notes are anonymous public input.** The API decodes them server-side, so they are rendered
 * verbatim as **plain text** — never `dangerouslySetInnerHTML`. Server-side decoding changed the
 * encoding, not the fact that anyone on the internet can put anything in this field.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon, Avatar, Badge, Button, Card, Center, Divider, Group, Loader, SimpleGrid, Stack,
  Text, Title, Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft, IconBook, IconCalendar, IconCheck, IconInfoCircle, IconMail, IconMapPin,
  IconPhone, IconRefresh, IconUser, IconUsers, IconX,
} from '@tabler/icons-react';
import Tabs from '@/components/ui/navigation/Tabs';
import { tabStyle } from '@/components/ui/TabStyle';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationIntakeService, useRegistrationIntakeStatusService,
  type RegistrationIntakeModelView,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { formatSDKDate } from '@/utils/dateTime';
import { resolveAvatarSrc, getInitialsFromName } from '@/utils/avatar';
import { INTAKE_STATUS } from '@/pages/Grow/intakeStatusIds';
import SubmittedPersonModal, { type SubmittedPerson } from '@/components/common/SubmittedPersonModal';
import { formatBirthDate } from '@/components/common/MemberDetailModal';
import { useLookupStatuses } from '@/pages/Overview/Hooks/useLookupStatuses';
import ApproveIntakeModal from '../Components/ApproveIntakeModal';
import RejectIntakeModal from '../Components/RejectIntakeModal';
import IntakeStatusMenu from '../Components/IntakeStatusMenu';

/** Label/value line. Duplicated per detail page in this repo rather than shared. */
function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <IconSlot>{icon}</IconSlot>
      <div style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed">{label}</Text>
        {empty
          ? <Text size="sm" c="dimmed">Not set</Text>
          : typeof value === 'string' || typeof value === 'number'
            ? <Text size="sm" fw={600}>{value}</Text>
            : value}
      </div>
    </Group>
  );
}

/** Keeps DetailRow's icon column from collapsing, without importing another primitive. */
function IconSlot({ children }: { children: React.ReactNode }) {
  return <div style={{ flexShrink: 0, opacity: 0.7, paddingTop: 2 }}>{children}</div>;
}

export default function IntakeDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const intakeService = useRegistrationIntakeService();
  const intakeStatusService = useRegistrationIntakeStatusService();
  const statuses = useLookupStatuses(intakeStatusService);
  const { refetch: refetchSidebar } = useSidebarCountsContext();

  const [intake, setIntake] = useState<RegistrationIntakeModelView | null>(null);
  const [loading, setLoading] = useState(true);
  const [changingStatus, setChangingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  /** Card click opens the read-only person detail. Null when closed. */
  const [detailPerson, setDetailPerson] = useState<SubmittedPerson | null>(null);

  const fetchIntake = useCallback(async () => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    try {
      setIntake(await intakeService.getById(Number(id)));
    } catch (e) {
      console.error('[IntakeDetails] failed to load:', e);
      setIntake(null);
    } finally {
      setLoading(false);
    }
  }, [id, intakeService]);

  useEffect(() => { fetchIntake(); }, [fetchIntake]);

  const handleChangeStatus = async (statusID: number) => {
    if (!intake) return;
    setChangingStatus(true);
    try {
      await intakeService.changeStatus(intake.intakeID, statusID);
      toast.success('Status updated', 'The intake status was changed.');
      await fetchIntake();
      refetchSidebar();
    } catch (e) {
      console.error('[IntakeDetails] status change failed:', e);
      toast.error('Could not change the status', e instanceof Error ? e.message : String(e));
    } finally {
      setChangingStatus(false);
    }
  };

  const handleApproved = async (registrationID: number | null) => {
    await fetchIntake();
    refetchSidebar();
    toast.success(
      'Intake approved',
      registrationID
        ? `Registration #${registrationID} was created.`
        : 'The registration was created.',
      registrationID
        ? {
          action: {
            label: 'View registration',
            onClick: () => navigate(`/Grow/Registration/RegistrationDetails/${registrationID}`),
          },
        }
        : undefined,
    );
  };

  const handleRejected = async () => {
    await fetchIntake();
    refetchSidebar();
  };

  const isPending = intake?.intakeStatusID === INTAKE_STATUS.Pending;
  const isDecided = useMemo(
    () => intake != null && intake.intakeStatusID !== INTAKE_STATUS.Pending,
    [intake],
  );

  // ⚠️ Rendered verbatim: the API decodes `notes` server-side, so decoding again here would corrupt
  // any text that merely happens to look Base64.
  const notes = intake?.notes?.trim() ?? '';
  const participants = intake?.participants ?? [];
  /**
   * False when someone signed another person up (SDK 0.0.56).
   *
   * ⚠️ Then the applicant is the **contact only**, not a participant — the same distinction the care
   * request page draws with its own `isForSelf`. Optional, and older intakes predate it, so an
   * explicit `=== false` avoids treating "not stated" as "for someone else".
   */
  const isForSelf = intake?.isForSelf !== false;

  if (!id) return null;

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading intake…</Text>
        </Stack>
      </Center>
    );
  }

  if (!intake) {
    return (
      <Stack gap="lg">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
          // ⚠️ `Stack` defaults to `align="stretch"`, which makes the button full width and centres
          // its label.
          style={{ alignSelf: 'flex-start' }}
        >
          Back
        </Button>
        <Center py="xl"><Text c="dimmed">Intake not found.</Text></Center>
      </Stack>
    );
  }

  const applicantName = intake.fullName
    || [intake.firstName, intake.surname].filter(Boolean).join(' ').trim()
    || 'Unknown';

  /**
   * The main applicant as a clickable person, for the case where no separate participants were
   * captured. Their match lives at the top of the intake (`matchedMemberID`), not in `participants`.
   * ⚠️ `id: 0` is a placeholder — there is no `participantID` for the applicant themselves, and the
   * id is only used as a React key here.
   */
  const applicantAsPerson: SubmittedPerson = {
    id: 0,
    firstName: intake.firstName,
    surname: intake.surname,
    email: intake.email,
    phone: intake.phone,
    altContact: intake.altContact,
    gender: intake.gender,
    isMain: true,
    memberID: intake.matchedMemberID,
    memberName: intake.matchedMemberName,
  };

  return (
    <Stack gap="lg">
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(-1)}
        // ⚠️ `Stack` defaults to `align="stretch"`, which makes the button full width and centres its
        // label.
        style={{ alignSelf: 'flex-start' }}
      >
        Back
      </Button>

      <Group justify="space-between" align="center">
        <Title order={2}>Intake Details</Title>
        <Group gap="xs" align="center">
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" size="lg" loading={loading} onClick={fetchIntake}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <IntakeStatusMenu
            statuses={statuses}
            currentStatusID={intake.intakeStatusID}
            busy={changingStatus}
            onChangeStatus={handleChangeStatus}
            onApprove={() => setApproveOpen(true)}
            onReject={() => setRejectOpen(true)}
          />
        </Group>
      </Group>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md" align="flex-start" wrap="nowrap">
            <Avatar radius="md" size={72}>{getInitialsFromName(applicantName)}</Avatar>
            <Stack gap={6}>
              <Title order={4}>{applicantName}</Title>
              <Group gap={6} wrap="wrap">
                <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(intake.statusColor)}>
                  {intake.statusName ?? `Status ${intake.intakeStatusID}`}
                </Badge>
                <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(intake.courseColor)}>
                  {intake.courseName ?? 'Course'}
                </Badge>
                {intake.isExistingMember
                  ? <Badge variant="outline" color="teal">Existing member</Badge>
                  : <Badge variant="outline" color="gray">New contact</Badge>}
                {participants.length > 1 && (
                  <Badge variant="outline" color="blue">{participants.length} people</Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {intake.reference ?? `Intake #${intake.intakeID}`}
                {intake.ageDisplay ? ` · submitted ${intake.ageDisplay} ago` : ''}
              </Text>
            </Stack>
          </Group>

          {/* The top-right corner is the primary action's, per the house convention. */}
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {isPending ? (
              <>
                <Button variant="light" color="red" onClick={() => setRejectOpen(true)}>Reject</Button>
                <Button leftSection={<IconCheck size={16} />} onClick={() => setApproveOpen(true)}>
                  Approve &amp; enrol
                </Button>
              </>
            ) : intake.registrationID ? (
              <Button
                variant="light"
                onClick={() => navigate(`/Grow/Registration/RegistrationDetails/${intake.registrationID}`)}
              >
                View registration #{intake.registrationID}
              </Button>
            ) : null}
          </Group>
        </Group>
      </Card>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={16} />} style={tabStyle(activeTab === 'overview')}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="people" leftSection={<IconUsers size={16} />} style={tabStyle(activeTab === 'people')}>
            People ({participants.length})
          </Tabs.Tab>
          {isDecided && (
            <Tabs.Tab value="decision" leftSection={<IconBook size={16} />} style={tabStyle(activeTab === 'decision')}>
              Decision
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="sm">
          <Tabs.Panel value="overview" pt="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Applicant</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow icon={<IconUser size={18} />} label="Name" value={applicantName} />
                  <DetailRow
                    icon={<IconUsers size={18} />}
                    label="Registering for"
                    value={isForSelf
                      ? <Badge size="sm" variant="light" color="teal">Themselves</Badge>
                      : <Badge size="sm" variant="light" color="orange">Someone else</Badge>}
                  />
                  <DetailRow icon={<IconMail size={18} />} label="Email" value={intake.email} />
                  <DetailRow icon={<IconPhone size={18} />} label="Phone" value={intake.phone} />
                  <DetailRow icon={<IconPhone size={18} />} label="Alternative contact" value={intake.altContact} />
                  <DetailRow icon={<IconUser size={18} />} label="Gender" value={intake.gender} />
                  <DetailRow
                    icon={<IconUser size={18} />}
                    label="Matched member"
                    value={intake.matchedMemberName}
                  />
                </Stack>
              </Card>

              <Card withBorder radius="md" padding="lg">
                <Text fw={700} mb="sm">Course</Text>
                <Divider mb="md" />
                <Stack gap="md">
                  <DetailRow icon={<IconBook size={18} />} label="Course" value={intake.courseName} />
                  <DetailRow
                    icon={<IconCalendar size={18} />}
                    label="Intake runs"
                    value={[formatSDKDate(intake.startDateTime), formatSDKDate(intake.endDateTime)]
                      .filter(Boolean).join(' to ')}
                  />
                  <DetailRow icon={<IconMapPin size={18} />} label="Location" value={intake.locationName} />
                  <DetailRow
                    icon={<IconCalendar size={18} />}
                    label="Submitted"
                    value={formatSDKDate(intake.submittedDateTime)}
                  />
                  <DetailRow icon={<IconInfoCircle size={18} />} label="Source" value={intake.source} />
                  <DetailRow
                    icon={intake.hasConsent ? <IconCheck size={18} /> : <IconX size={18} />}
                    label="Consent given"
                    value={
                      <Badge size="sm" variant="light" color={intake.hasConsent ? 'green' : 'red'}>
                        {intake.hasConsent ? 'Yes' : 'No'}
                      </Badge>
                    }
                  />
                </Stack>
              </Card>
            </SimpleGrid>

            <Card withBorder radius="md" padding="lg" mt="lg">
              <Text fw={700} mb="sm">What they said</Text>
              <Divider mb="md" />
              {notes ? (
                // Plain text on purpose: anonymous public input, never rendered as HTML.
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{notes}</Text>
              ) : (
                <Text size="sm" c="dimmed">No notes were given.</Text>
              )}
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="people" pt="md">
            <Card withBorder radius="md" padding="lg">
              <Text fw={700} mb="sm">Who is signing up</Text>
              <Text size="sm" c="dimmed" mb="md">
                Everyone here becomes a registration member on approval, matched to an existing member
                where one is found. Select a person to see their full details.
              </Text>
              {/* The applicant is not among them when they signed someone else up, so name them. */}
              {!isForSelf && participants.length > 0 && (
                <Text size="sm" c="dimmed" mb="md">
                  Registered by <Text span fw={600}>{applicantName}</Text>
                  {intake.email || intake.phone ? ` · ${intake.email || intake.phone}` : ''}, who is the
                  contact rather than a participant.
                </Text>
              )}
              <Divider mb="md" />

              {participants.length === 0 ? (
                <Stack gap="md">
                  {/* ⚠️ With no participants and `isForSelf === false` the intake names a contact but
                      nobody to enrol. Saying "for the applicant alone" there would be wrong. */}
                  <Text size="sm" c="dimmed">
                    {isForSelf
                      ? 'No extra participants were captured, so this intake is for the applicant alone.'
                      : 'This sign-up was made on behalf of someone else, but no participant was captured. The applicant below is the contact, not the student.'}
                  </Text>
                  <Card
                    withBorder
                    radius="md"
                    padding="md"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setDetailPerson(applicantAsPerson)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setDetailPerson(applicantAsPerson);
                      }
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Avatar radius="xl" size="md">{getInitialsFromName(applicantName)}</Avatar>
                      <div style={{ minWidth: 0 }}>
                        <Group gap={6}>
                          <Text size="sm" fw={600}>{applicantName}</Text>
                          <Badge size="xs" variant="light" color={isForSelf ? 'blue' : 'gray'}>
                            {isForSelf ? 'Main' : 'Contact only'}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed">{intake.email || intake.phone || 'No contact'}</Text>
                      </div>
                    </Group>
                  </Card>
                </Stack>
              ) : (
                <Stack gap="sm">
                  {participants.map((p) => {
                    const name = p.fullName
                      || [p.name, p.surname].filter(Boolean).join(' ').trim()
                      || 'Unnamed';
                    return (
                      <Card
                        key={p.participantID}
                        withBorder
                        radius="md"
                        padding="md"
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setDetailPerson({
                          id: p.participantID,
                          firstName: p.name,
                          surname: p.surname,
                          email: p.email,
                          phone: p.phone,
                          gender: p.gender,
                          dateOfBirth: p.dateOfBirth,
                          role: p.role,
                          isMember: p.isMember,
                          otherChurch: p.otherChurch,
                          displayLocationName: p.displayLocationName,
                          isMain: p.isMain,
                          memberID: p.memberID,
                        })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDetailPerson({
                              id: p.participantID,
                              firstName: p.name,
                              surname: p.surname,
                              email: p.email,
                              phone: p.phone,
                              gender: p.gender,
                              dateOfBirth: p.dateOfBirth,
                              role: p.role,
                              isMember: p.isMember,
                              otherChurch: p.otherChurch,
                              displayLocationName: p.displayLocationName,
                              isMain: p.isMain,
                              memberID: p.memberID,
                            });
                          }
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                            <Avatar radius="xl" size="md">{getInitialsFromName(name)}</Avatar>
                            <div style={{ minWidth: 0 }}>
                              <Group gap={6}>
                                <Text size="sm" fw={600}>{name}</Text>
                                {p.isMain && <Badge size="xs" variant="light" color="blue">Main</Badge>}
                                {p.role && <Badge size="xs" variant="outline">{p.role}</Badge>}
                              </Group>
                              <Text size="xs" c="dimmed" truncate>
                                {[p.email, p.phone].filter(Boolean).join(' · ') || 'No contact'}
                              </Text>
                              {/* Server-computed affiliation — location, other church, or
                                  "Not a member". Not derived here, so both queues word it alike. */}
                              {p.displayLocationName && (
                                <Text size="xs" c="dimmed" truncate>{p.displayLocationName}</Text>
                              )}
                            </div>
                          </Group>
                          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                            {p.gender && <Badge size="xs" variant="outline">{p.gender}</Badge>}
                            {p.dateOfBirth && (
                              <Badge size="xs" variant="outline" color="gray">
                                {formatBirthDate(p.dateOfBirth) ?? p.dateOfBirth}
                              </Badge>
                            )}
                            <Badge
                              size="xs"
                              variant="light"
                              color={p.memberID ? 'teal' : 'gray'}
                            >
                              {p.memberID ? `Member #${p.memberID}` : 'New contact'}
                            </Badge>
                          </Group>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          {isDecided && (
            <Tabs.Panel value="decision" pt="md">
              <Stack gap="lg">
                <Card withBorder radius="md" padding="lg">
                  <Text fw={700} mb="sm">Decision</Text>
                  <Divider mb="md" />
                  <Stack gap="md">
                    <DetailRow
                      icon={
                        <Avatar src={resolveAvatarSrc(intake.decidedByAvatar)} size={18} radius="xl">
                          {getInitialsFromName(intake.decidedByName ?? '')}
                        </Avatar>
                      }
                      label="Decided by"
                      value={intake.decidedByName}
                    />
                    <DetailRow
                      icon={<IconCalendar size={18} />}
                      label="Decided on"
                      value={intake.decidedDateTime ? formatSDKDate(intake.decidedDateTime) : undefined}
                    />
                    <DetailRow
                      icon={<IconInfoCircle size={18} />}
                      label="Reason"
                      value={intake.decisionReason}
                    />
                    {intake.registrationID && (
                      <DetailRow
                        icon={<IconBook size={18} />}
                        label="Registration created"
                        value={
                          <Button
                            size="compact-sm"
                            variant="light"
                            onClick={() => navigate(`/Grow/Registration/RegistrationDetails/${intake.registrationID}`)}
                          >
                            Registration #{intake.registrationID}
                          </Button>
                        }
                      />
                    )}
                  </Stack>
                </Card>
              </Stack>
            </Tabs.Panel>
          )}
        </Card>
      </Tabs>

      <ApproveIntakeModal
        intake={intake}
        opened={approveOpen}
        onClose={() => setApproveOpen(false)}
        onApproved={handleApproved}
      />
      <RejectIntakeModal
        intake={intake}
        opened={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onRejected={handleRejected}
      />
      <SubmittedPersonModal
        person={detailPerson}
        opened={detailPerson !== null}
        onClose={() => setDetailPerson(null)}
        contextLabel="intake"
        hasConsent={intake.hasConsent}
      />
    </Stack>
  );
}
